import { alpha, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ViewMode } from '../../types';

export const THUMBNAIL_GRID_HEIGHT = 190;
const ITEM_GRID_LIST_WIDTH = 277;

const getGridListStyle = (viewMode: ViewMode, theme: Theme) => {
  if (viewMode === ViewMode.GRID_HORIZONTAL) {
    return {
      backgroundColor: theme.palette.common.white,
      border: `solid 1px ${theme.palette.grey[100]}`,
      display: 'grid',
      gridAutoFlow: 'column',
      gridGap: '16px',
      gridTemplateColumns: `${ITEM_GRID_LIST_WIDTH}px !important`,
      margin: 0,
      overflowX: 'auto',
      padding: theme.spacing(3, 3, 4),
      zoom: 0.7,
    };
  }

  return {
    overflowY: 'hidden',
    paddingTop: '1px',
  };
};

interface GridItemStyledProps {
  allowSearch?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  showFooterInfos?: boolean;
  title?: string | null;
  viewMode: ViewMode;
}

export const useStyles = makeStyles<Theme, GridItemStyledProps>((theme) => ({
  barSubtitle: ({ isDisabled, title, viewMode }) => ({
    color:
      isDisabled && viewMode === ViewMode.GRID
        ? theme.palette.grey['500']
        : title?.length
        ? theme.palette.grey['800']
        : theme.palette.grey['300'],
    fontWeight: 500,
    fontSize: 16,
    marginTop: theme.spacing(0.75),
  }),
  barTitle: {
    color: theme.palette.grey['500'],
    fontSize: '.7rem',
    lineHeight: '12px',
    marginTop: theme.spacing(0.75),
    paddingLeft: 0,
  },
  barTitleEmpty: {
    color: theme.palette.grey['300'],
    fontStyle: 'italic',
  },
  busyIcon: {
    minWidth: 'auto',
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(1.5),
    zIndex: 1,
  },
  centered: {
    alignItems: 'center',
    display: 'flex',
    left: '50%',
    position: 'absolute',
    textAlign: 'center',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  deleteIcon: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    zoom: 1.3,
    zIndex: 1,
  },
  eyeIcon: {
    color: 'white',
  },
  gridList: ({ viewMode }: GridItemStyledProps) => ({
    gap: `${theme.spacing(2)} !important`,
    gridAutoColumns: `${ITEM_GRID_LIST_WIDTH}px`,
    justifyContent: 'start',
    ...(getGridListStyle(viewMode, theme) as any),
    marginTop: '0px',
  }),
  rootTileBar: {
    backgroundColor: theme.palette.background.paper,
  },
  selectedIcon: ({ isDisabled, isSelected }) => ({
    minWidth: 'auto',
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(1.5),
    zIndex: 1,

    '& > svg': {
      backgroundColor:
        isSelected && !isDisabled
          ? theme.palette.primary.main
          : theme.palette.grey[400],
      borderRadius: '50%',
      color: theme.palette.common.white,
      opacity: isSelected && !isDisabled ? 1 : 0.5,
    },
  }),
  topBarRoot: {
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.grey[800], 0.7),
    borderRadius: 2,
    color: 'white',
    display: 'flex',
    height: 24,
    left: theme.spacing(1),
    padding: theme.spacing(0, 0.5),
    position: 'absolute',
    right: 'auto',
    top: theme.spacing(1),
    zIndex: 0,
    zoom: ({ viewMode }) => (viewMode === ViewMode.GRID_HORIZONTAL ? 1.3 : 0),
  },
  topBarTitle: {
    paddingLeft: 0,
    paddingRight: 0,

    '& > div': {
      fontSize: '.85rem',
      lineHeight: '22px',
    },
  },
  wrapperCard: ({ isDisabled }) => ({
    border: `solid 1px ${theme.palette.grey[300]}`,
    borderRadius: '4px',
    cursor: 'pointer',
    maxWidth: '290px',
    overflow: 'hidden',
    pointerEvents: isDisabled ? 'none' : 'auto',
  }),
  wrapperGrid: ({ viewMode }) => ({
    height: '100%',
    overflow: 'auto',
    ...(viewMode === ViewMode.GRID
      ? {
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3.3),
          paddingTop: '10px',
        }
      : {
          borderBottomLeftRadius: '5px',
          borderBottomRightRadius: '5px',
          padding: 0,
        }),
    willChange: 'scroll-position',
  }),
  wrapperMedia: ({ isDisabled, showFooterInfos }) => ({
    backgroundColor: theme.palette.grey[300],
    backgroundPosition: 'top center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    filter: `grayscale(${isDisabled ? 1 : 0})`,
    height: showFooterInfos
      ? `calc(${THUMBNAIL_GRID_HEIGHT}px - 50px)`
      : `${THUMBNAIL_GRID_HEIGHT}px`,
    width: '100%',
  }),
}));
